import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toggleEditMode, updateWidgetProperty, updateWidgetContent, updateWidgetLayout, updateWidgetActions, actionGroup } from 'actions/editor';
import { calculateBoundsForElement, getParentContainerOfType, isWidgetHasHotspotChild, getWidgetType } from 'components/Editor/helper/widget';
import { CONTENT_CARD, HOTSPOT, PAGE, SECTION_COLUMN, BASIC_TEXT, CONTENT, CONTAINER } from 'constants/widgetTypes';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from "scenejs";

import { EditorContext } from '../../index';
import { getActionsFromHTML } from '../../helper/text';

import { Property } from '../../components/properties/';
import { Animation, AnimationProperty } from 'components/Editor/components/animations';
import { Font } from '../../components/properties/fonts';
import { ContentWidgetContext } from '../content';

import { faAlignCenter, faAlignJustify, faAlignLeft, faAlignRight, faBold, faFill, faFont, faItalic, faLink, faMinus, faPlus, faQuestionCircle, faTint, faUnderline } from '@fortawesome/free-solid-svg-icons'

import { associateFontsWithWigdet } from 'actions/fonts';
import { Fonts } from '@paperflite-marketing/cleverstory-commons';

import styles from './basicText.module.scss';


const BasicTextWidgetViewComponent = function BasicTextEditor({ className, style, widgetProps, widgetRef }) {
  const dispatch = useDispatch();
  const ref = widgetRef;
  const editorRef = React.useRef(null);

  const [canEdit, setCanEdit] = useState(false);

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const name = _.get(widgetProps, 'name');
  const content = widgetProps.content;
  const contentContainer = getParentContainerOfType(editor, id, CONTENT);
  const countText = type === BASIC_TEXT && name === 'Text' && contentContainer;
  const contentWidgetContext = React.useContext(ContentWidgetContext);
  const contentType = _.get(contentWidgetContext, 'contentType', '');

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));

  const editMode = useSelector(state => {
    return _.get(state, `${editor}.present.editMode`) === id
  });

  const selected = useSelector(state => {
    const selected = _.get(state, `${editor}.present.selected`);
    if (_.size(selected) != 1) {
      return false;
    }

    return _.includes(selected, id);
  });

  Fonts.useFont(_.get(widgetProps, 'fonts') || []);

  useEffect(() => {
    if (editorRef.current) {
      if (editMode) {
        editorRef.current.focus();
      } else {
        //editorRef.current.blur();
      }
    }
  }, [editMode])

  useEffect(() => {
    if (!selected) {
      setCanEdit(false);
    }

  }, [selected]);

  const onChange = (e) => {
    const updatedContent = e.target.value;
    if (updatedContent != content) {
      dispatch(updateWidgetContent(editor, id, updatedContent));
    }
  }

  const handleContentClicked = (e) => {
    if (!canEdit) {
      setCanEdit(true);
    }

    if (canEdit && selected) {
      if (editorRef.current) {
        if (editorRef.current == document.activeElement) {
          return;
        }

        dispatch(toggleEditMode(editor, id, true));

        const end = editorRef.current.value.length;
        editorRef.current.setSelectionRange(end, end);
        editorRef.current.focus();
      }
    }
  }

  var widgetStyle = {
    ...getStyles(widgetProps, breakpoint)
  }

  const removeHtmlTags = (htmlString) => {
    if(!htmlString) {
      return '';
    }
    
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    return _.get(doc, 'body.textContent', '');
  };

  return (
    <div id={id}
      ref={ref}
      className={cx(className, styles.basicText, 'widget', {
        [styles.selected]: selected
      })}
      style={style}
      data-container={false}
      data-widget-id={id}
      data-widget-type={type}
      data-selectable={true}
      onClick={handleContentClicked}
    >
      <Animation widget={widgetProps}>
        <div className={styles.inner} style={widgetStyle}>
          {breakpoint === 'desktop' && selected &&
            <textarea
              disabled={contentType === 'DYNAMIC' || !!countText}
              ref={editorRef}
              className={styles.editor}
              style={widgetStyle}
              value={removeHtmlTags(content)}
              onChange={onChange}
            ></textarea>
          }
          {(breakpoint === 'mobile' || !selected) &&
            <>{ removeHtmlTags(content) }</>
          }
        </div>
      </Animation>
    </div>
  );
};

const basicTextWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'widgetRef'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const BasicTextWidgetView = React.memo(BasicTextWidgetViewComponent, basicTextWidgetEquals);

const BasicTextWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const id = _.get(props, 'uid');

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const handleEditMode = () => {
    dispatch(toggleEditMode(editorSession, props.uid, true));
    setView(null)
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  return (
    <>
      <Property.Action className={styles.textButton} onClick={handleEditMode.bind(this, 'text')}>Change Text</Property.Action>
      <Property.Divider />
      <Property.Action className={styles.iconButton} tooltip='Animation' onClick={() => changeView('animation')}>
        <i className={cx("cleverstory-animations")} />
      </Property.Action>

      {editMode &&
        <BasicTextStyleEditor
          key={`style_editor_${id}`}
          id={id}
          editorSession={editorSession}
          {...props}
        />
      }

      {!editMode && view === 'animation' &&
        <AnimationProperty
          ids={[id]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[props]}
          onCancel={() => {
            closePopup()
          }}
        />
      }

    </>
  );
}

const BasicTextWidgetMobileAction = ({ editorSession, uid, ...rest }) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);

  const properties = _.get(rest, `properties.mobile`, {});
  const font = _.get(properties, 'font');
  const fontSize = _.get(font, 'fontSize');

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === uid
  });

  const handleEditMode = () => {
    dispatch(toggleEditMode(editorSession, uid, true));
  }

  const handleSizeChange = (value) => {
    const request = {
      font: {
        ...font,
        fontSize: value
      }
    }

    dispatch(updateWidgetProperty(editorSession, uid, 'mobile', request));
  }

  const increaseFontSize = () => {
    var value = Math.min(Math.floor(fontSize) + 1, 150);
    handleSizeChange(value);
  }

  const decreaseFontSize = () => {
    var value = Math.max(Math.floor(fontSize) - 1, 6);
    handleSizeChange(value);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, uid, false));
    setView(null);
  }

  let widget = {
    uid: uid,
    ...rest
  }

  return (
    <>
      <Property.Action className={styles.iconButton} tooltip='Increase Size' onClick={increaseFontSize}>
        <i className={cx("cleverstory-text-maximize")} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip='Decrease Size' onClick={decreaseFontSize}>
        <i className={cx("cleverstory-text-minimize")} />
      </Property.Action>
      <Property.Divider />
      <Property.Action className={styles.iconButton} tooltip='Animation' onClick={() => setView('animation')}>
        <i className={cx("cleverstory-animations")} />
      </Property.Action>
      <Property.Divider />
      <Property.Action className={styles.iconButton} tooltip='Setting' onClick={handleEditMode}>
        <i className={cx("cleverstory-settings", styles.settingsIcon)} />
      </Property.Action>

      {editMode &&
        <BasicTextMobileStyleEditor
          id={uid}
          editorSession={editorSession}
          {...widget}
        />
      }

      {!editMode && view === 'animation' &&
        <AnimationProperty
          ids={[uid]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[widget]}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  );
}

const BasicTextWidget = React.forwardRef((props, ref) => {
  return (
    <BasicTextWidgetView {...props} ref={ref} />
  )
});

BasicTextWidget.actions = (editorSession, breakpoint, props) => {
  return (
    <>
      {breakpoint === 'desktop' &&
        <BasicTextWidgetDesktopAction {...props} />
      }

      {breakpoint === 'mobile' &&
        <BasicTextWidgetMobileAction
          editorSession={editorSession}
          {...props}
        />
      }
    </>
  );
}

const BasicTextStyleEditor = ({ uid, editorSession, ...rest }) => {
  const dispatch = useDispatch();
  const type = _.get(rest, 'type');
  const name = _.get(rest, 'name');
  const widgetUid = _.get(rest, 'template.widget') || uid;
  const contentContainer = getParentContainerOfType(editorSession, uid, CONTENT);
  const countText = type === BASIC_TEXT && name === 'Text' && contentContainer;

  const properties = _.get(rest, `properties.desktop`, {});
  const font = _.get(properties, 'font');
  const fontAlignment = _.get(properties, `alignment`);
  const background = _.get(properties, `background`);

  const fontFamily = _.get(font, 'fontFamily');
  const fontSize = _.get(font, 'fontSize');
  const lineSpacing = _.get(font, 'lineSpacing');
  const letterSpacing = _.get(font, 'letterSpacing') || 0;
  const color = _.get(font, 'color');

  const bold = _.get(font, 'style.bold');
  const italic = _.get(font, 'style.italic');
  const underline = _.get(font, 'style.underline');

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, uid, 'desktop', value));
  }

  const handleFontFamily = (value) => {
    const fontId = _.get(value, 'font.id');
    var family = _.get(value, 'value');
    if (!family) {
      return;
    }

    const request = {
      font: {
        ...font,
        fontFamily: family
      }
    }

    handlePropertyChange(request);

    if (fontId) {
      dispatch(associateFontsWithWigdet(editorSession, widgetUid, [fontId]));
    }
  }

  const handleAlignmentChange = (value) => {
    var alignment = _.get(value, 'value');
    if (!alignment) {
      return;
    }

    handlePropertyChange({
      alignment: alignment
    });
  }

  const handleSizeChange = (value) => {
    const request = {
      font: {
        ...font,
        fontSize: value
      }
    }

    handlePropertyChange(request);
  }

  const handleSpacingChange = (value) => {
    const request = {
      font: {
        ...font,
        ...value
      }
    }

    handlePropertyChange(request);
  }

  const handleBoldToggle = () => {
    const current = _.get(font, 'style.bold');
    const existingStyle = _.get(font, 'style') || {};
    const request = {
      font: {
        ...font || {},
        style: {
          ...existingStyle,
          bold: !current
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleItalicsToggle = () => {
    const current = _.get(font, 'style.italic');
    const existingStyle = _.get(font, 'style') || {};

    const request = {
      font: {
        ...font || {},
        style: {
          ...existingStyle,
          italic: !current
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleUnderlineToggle = () => {
    const current = _.get(font, 'style.underline');
    const existingStyle = _.get(font, 'style') || {};

    const request = {
      font: {
        ...font || {},
        style: {
          ...existingStyle,
          underline: !current
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleColorChange = (colorRgb) => {
    const request = {
      font: {
        ...font,
        color: colorRgb
      }
    }

    handlePropertyChange(request);
  }

  const handleBackgroundChange = (colorRgb) => {
    const request = {
      background: colorRgb
    }

    handlePropertyChange(request);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, uid, false));
  }

  return (
    <Property.Dialog
      className={styles.fontDialog}
      width={222}
      target={`#${uid}`}
      title='Text Settings'
      onCancel={closePopup}
    >
      <Property.Body>
        <Font.Family value={fontFamily} onChange={handleFontFamily} />
        <Font.Size min={6} max={300} value={fontSize} onChange={handleSizeChange} />
        <Property>
          <div className={styles.fontActions}>
            <Property.Action className={cx(styles.iconButton, { [styles.active]: bold })} tooltip="Bold" onClick={handleBoldToggle} >
              <i className={'cleverstory-bold'} />
            </Property.Action>
            <Property.Action className={cx(styles.iconButton, { [styles.active]: italic })} tooltip="Italic" onClick={handleItalicsToggle}>
              <i className={'cleverstory-italics'} />
            </Property.Action>
            <Property.Action className={cx(styles.iconButton, { [styles.active]: underline })} tooltip="Underline" onClick={handleUnderlineToggle}>
              <i className={'cleverstory-underline'} />
            </Property.Action>
            <Font.Color value={color} onChange={handleColorChange} />
            <Font.Background value={background} onChange={handleBackgroundChange} />
          </div>
        </Property>
        {!countText &&
          <Property name="Alignment">
            <div className={styles.alignmentActions}>
              <Font.Alignment value={fontAlignment} onChange={handleAlignmentChange} />
            </div>
          </Property>
        }
        <Font.Spacing value={{
          lineSpacing: lineSpacing,
          letterSpacing: letterSpacing
        }} onChange={handleSpacingChange} />
      </Property.Body>
    </Property.Dialog>
  );
}

const BasicTextMobileStyleEditor = ({ position, editorSession, ...rest }) => {
  const dispatch = useDispatch();

  const properties = _.get(rest, `properties.mobile`, {});
  const font = _.get(properties, 'font');

  const fontSize = _.get(font, 'fontSize');
  const lineSpacing = _.get(font, 'lineSpacing');
  const letterSpacing = _.get(font, 'letterSpacing');

  const spacing = {
    lineSpacing, letterSpacing
  }

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, rest.uid, 'mobile', value));
  }

  const handleSizeChange = (value) => {
    const request = {
      font: {
        ...font,
        fontSize: value
      }
    }

    handlePropertyChange(request);
  }

  const handleSpacingChange = (value) => {
    const request = {
      font: {
        ...font,
        ...value
      }
    }

    handlePropertyChange(request);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, rest.uid, false));
  }

  return (
    <Property.Dialog
      key={rest.uid}
      className={cx(styles.fontDialog, styles.mobile)}
      width={222}
      target={`#${rest.uid}`}
      title='Text Settings'
      onCancel={closePopup}
    >
      <Property.Body>
        <Font.Size min={6} max={150} value={fontSize} onChange={handleSizeChange} />
        <Font.Spacing value={spacing} onChange={handleSpacingChange} />
      </Property.Body>
    </Property.Dialog>
  );
}


BasicTextWidget.processRequest = (widget) => {
  var content = _.get(widget, 'content');
  var actions = _.get(widget, 'actions', []) || [];

  const actionIds = getActionsFromHTML(content) || [];
  const updated = actions.filter(action => _.includes(actionIds, action.uid));

  return {
    ...widget,
    actions: updated
  }
}

BasicTextWidget.moveableConfig = (editorSession, widget) => {
  const contentCard = getParentContainerOfType(editorSession, widget.uid, CONTENT_CARD);
  const countText = getWidgetType(editorSession, widget.parent) === CONTENT ? true : false;
  var bounds = null;
  
  if (contentCard) {
    bounds = calculateBoundsForElement(contentCard);
  }

  return {
    draggable: !countText,
    resizable: true,
    rotatable: !countText,
    selectable: true,
    cloneable: !countText && !contentCard,
    deletable: !countText && !contentCard,
    bounds: bounds,
    renderDirections: ["n", "s", "ne", "nw", "se", "sw", "e", "w"],
    onReparent: (widget, parent) => {
      var parentElement = parent ? document.getElementById(parent) : null;
      if (parentElement && parentElement.getAttribute('data-widget-type') === HOTSPOT) {
        return false;
      }

      const parentWidget = getParentContainerOfType(editorSession, widget, CONTENT_CARD);

      const hasHotspotChild = isWidgetHasHotspotChild(editorSession, widget);
      const parentType = getWidgetType(editorSession, parent);

      const denyHostpotReparent = hasHotspotChild && ![PAGE, SECTION_COLUMN, CONTAINER].includes(parentType);

      if (parentWidget || denyHostpotReparent) {
        return false;
      }

      return true;
    },
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      return frame.get();
    }
  }
}


/**
 * Generate Styles
 * 
*/
function getStyles(widget, breakpoint) {
  var styles = {};

  const defaultProperties = _.get(widget, `properties.desktop`);
  const properties = _.get(widget, `properties.${breakpoint}`);

  if (!properties) {
    return styles;
  }

  //Layout
  const width = _.get(properties, 'layout.width');
  const height = _.get(properties, 'layout.height');
  styles = {
    ...styles,
    width: width,
    height: height
  }

  //font-color
  const colorRgb = _.get(defaultProperties, `font.color`, null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }

  //background
  const backgroundRgb = _.get(defaultProperties, `background`, null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = `rgba(${backgroundRgb.r}, ${backgroundRgb.g}, ${backgroundRgb.b}, ${backgroundRgb.a})`;
  }

  styles['backgroundColor'] = background;

  const defaultFont = _.get(defaultProperties, 'font', {});
  const font = _.get(properties, 'font', {});

  //font-family
  const fontFamily = _.get(defaultFont, 'fontFamily');
  if (fontFamily) {
    styles = {
      ...styles,
      fontFamily: fontFamily
    }
  }

  //font-size
  const fontSizeString = _.get(font, 'fontSize', null);
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px';
  }

  styles['fontSize'] = fontSize;

  //line spacing
  const lineSpacingString = _.get(font, 'lineSpacing', null);
  if (lineSpacingString) {
    const lineSpacing = parseFloat(lineSpacingString) + 'em';
    styles['lineHeight'] = lineSpacing;
  }

  //Letter Spacing
  const letterSpacingString = _.get(font, 'letterSpacing', null);
  if (letterSpacingString) {
    const letterSpacing = parseFloat(letterSpacingString) + 'em';
    styles['letterSpacing'] = letterSpacing;
  }

  //font-style
  const bold = _.get(defaultFont, 'style.bold', false);
  if (bold) {
    styles['fontWeight'] = 'bold';
  }

  const italic = _.get(defaultFont, 'style.italic', false);
  if (italic) {
    styles['fontStyle'] = 'italic';
  }

  const underline = _.get(defaultFont, 'style.underline', false);
  if (underline) {
    styles['textDecoration'] = 'underline';
  }

  //alignment
  const alignment = _.get(properties, 'alignment', 'left');
  styles['textAlign'] = alignment;

  return styles;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  var styles = {
    color: color
  };

  return styles;
}


export { BasicTextWidget };